import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES, getTestingCandidateMgmtEditRoute } from "@/constants/routes";
import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_components/table";
import { Edit, Trash2, Plus } from "lucide-react";
import { toast } from "react-hot-toast";
import { UI_MESSAGES } from "@/constants/ui-constants";
import { customInstance } from "@/lib/axios-instance";
import { API_CONFIG } from "@/constants/api";
import { isAxiosError } from "axios";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";

interface Candidate {
  id: number;
  recipientId?: string;
  fullName: string;
  mobile: string;
  email: string;
  title?: string;
  totalExperience?: string;
  onboardingStatus?: string;
}

const CandidateMgmtPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_TESTING_VIEW">
      <CandidateMgmtContent />
    </PageAccessGuard>
  );
};

const CandidateMgmtContent: React.FC = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const hasFetched = useRef(false);

  // Fetch candidates
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchCandidates();
    }
  }, []);

  const fetchCandidates = async () => {
    try {
      setIsLoading(true);

      const response = await customInstance<Candidate[]>({
        url: `${API_CONFIG.ENDPOINTS.TEMP_CANDIDATE}`,
        method: 'GET'
      });

      setCandidates(response.data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      hasFetched.current = false; // Reset on error to allow retry

      // Extract error message from API response
      let errorMessage = "Failed to load candidates";
      if (isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; error?: string } | undefined;
        if (responseData?.message) {
          errorMessage = `Failed to load candidates: ${responseData.message}`;
        } else if (responseData?.error) {
          errorMessage = `Failed to load candidates: ${responseData.error}`;
        } else if (error.message) {
          errorMessage = `Failed to load candidates: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = `Failed to load candidates: ${error.message}`;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this candidate?')) {
      return;
    }

    try {
      setIsDeleting(id);

      await customInstance({
        url: `/api/user/temp/candidate/id/${id}`,
        method: 'DELETE'
      });

      toast.success('Candidate deleted successfully');

      // Update local state
      setCandidates(prev => prev.filter(candidate => candidate.id !== id));
    } catch (error) {
      console.error('Error deleting candidate:', error);

      // Extract error message from API response
      let errorMessage = "Failed to delete candidate";
      if (isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; error?: string } | undefined;
        if (responseData?.message) {
          errorMessage = `Failed to delete candidate: ${responseData.message}`;
        } else if (responseData?.error) {
          errorMessage = `Failed to delete candidate: ${responseData.error}`;
        } else if (error.message) {
          errorMessage = `Failed to delete candidate: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = `Failed to delete candidate: ${error.message}`;
      }

      toast.error(errorMessage);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleEdit = (id: number) => {
    navigate(getTestingCandidateMgmtEditRoute(id));
  };

  if (isLoading) {
    return <div className="p-6">{UI_MESSAGES.LOADING}</div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Candidate Management</h1>
            <p className="text-gray-600 mt-2">Manage candidates and their information</p>
          </div>
          <Button
            onClick={() => navigate(ROUTES.TESTING_CANDIDATE_MGMT_CREATE)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Candidate
          </Button>

          <Button
            onClick={() => navigate(ROUTES.TESTING_CANDIDATE_MGMT_CREATE)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Test Middleware API
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Candidates</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="font-semibold text-gray-900">ID</TableHead>
                  <TableHead className="font-semibold text-gray-900">Full Name</TableHead>
                  <TableHead className="font-semibold text-gray-900">Title</TableHead>
                  <TableHead className="font-semibold text-gray-900">Email</TableHead>
                  <TableHead className="font-semibold text-gray-900">Mobile</TableHead>
                  <TableHead className="font-semibold text-gray-900">Experience</TableHead>
                  <TableHead className="font-semibold text-gray-900">Status</TableHead>
                  <TableHead className="font-semibold text-gray-900">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.map((candidate) => (
                  <TableRow key={candidate.id}>
                    <TableCell className="font-medium">{candidate.id}</TableCell>
                    <TableCell className="font-medium">{candidate.fullName}</TableCell>
                    <TableCell>{candidate.title || 'N/A'}</TableCell>
                    <TableCell>{candidate.email}</TableCell>
                    <TableCell>{candidate.mobile}</TableCell>
                    <TableCell>{candidate.totalExperience ? `${candidate.totalExperience} years` : 'N/A'}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        candidate.onboardingStatus === 'Not Started' 
                          ? 'bg-gray-100 text-gray-800'
                          : candidate.onboardingStatus === 'In Progress'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {candidate.onboardingStatus || 'Unknown'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(candidate.id)}
                          className="h-8 w-8 p-0"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(candidate.id)}
                          disabled={isDeleting === candidate.id}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {candidates.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No candidates found. Click "Add Candidate" to create your first candidate.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


export default CandidateMgmtPage;
